import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';

/** The gateway credentials a product's settings carry for the whitelabel microservice. */
export interface WhitelabelPartnerCredentials {
  productPid: number;
  accessToken: string;
  secretKey: string;
}

interface SettingsMicroservice {
  name?: string | null;
  baseUrl?: string | null;
  accessToken?: string | null;
  secretKey?: string | null;
}

interface SettingsProduct {
  id?: string | number;
  pid?: number | string;
  microservices?: SettingsMicroservice[] | null;
}

/** A microservice base URL without its `/whitelabel` suffix or trailing slash, for matching. */
function normalizeBase(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/whitelabel\/?$/i, '').replace(/\/$/, '');
    return `${parsed.origin}${path}`;
  } catch {
    return url.replace(/\/whitelabel\/?$/i, '').replace(/\/$/, '');
  }
}

/**
 * The whitelabel gateway's partner credentials, read from the settings (`get-setting`) of the
 * selected product. The old API hands them out and the gateway authenticates with them;
 * prism-new's get-setting sends none, so with it there is nothing here and every whitelabel call
 * belongs on prism-new.
 */
@Injectable({ providedIn: 'root' })
export class WhitelabelPartnerAuthService {
  private readonly apiService = inject(ApiService);

  /** The selected product's gateway credentials, or null when the settings carry none. */
  credentials(): WhitelabelPartnerCredentials | null {
    const selectedApiId =
      this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!selectedApiId) return null;

    const products = this.apiService.products() as SettingsProduct[];
    const product =
      this.productFromDynamicRoutes(String(selectedApiId)) ??
      products.find((p) => String(p.id) === String(selectedApiId));
    const productPid = Number(product?.pid);
    if (!Number.isFinite(productPid)) return null;

    const entry = this.gatewayEntry(product?.microservices ?? []);
    if (!entry?.accessToken || !entry.secretKey) return null;
    return { productPid, accessToken: entry.accessToken, secretKey: entry.secretKey };
  }

  /**
   * `url` with the `authToken`, `secretKey` and `productId` query params a gateway call needs,
   * when credentials exist; otherwise unchanged.
   */
  withQuery(url: string): string {
    const creds = this.credentials();
    if (!creds) return url;
    try {
      const parsed = new URL(url);
      if (!parsed.searchParams.has('authToken')) parsed.searchParams.set('authToken', creds.accessToken);
      if (!parsed.searchParams.has('secretKey')) parsed.searchParams.set('secretKey', creds.secretKey);
      if (!parsed.searchParams.has('productId')) parsed.searchParams.set('productId', String(creds.productPid));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /** The entry for the configured whitelabel gateway, else any entry carrying both values. */
  private gatewayEntry(list: SettingsMicroservice[]): SettingsMicroservice | undefined {
    const withCredentials = list.filter((m) => !!m.accessToken && !!m.secretKey);
    const targetBase = normalizeBase(environment.microAppService_url?.whitelabel?.url ?? '');
    return (
      withCredentials.find((m) => {
        const baseUrl = (m.baseUrl ?? '').trim();
        return !!baseUrl && normalizeBase(baseUrl) === targetBase;
      }) ?? withCredentials[0]
    );
  }

  private productFromDynamicRoutes(selectedApiId: string): SettingsProduct | null {
    const raw = sessionStorage.getItem('dynamic_routes');
    if (!raw) return null;
    try {
      const routes = JSON.parse(raw) as Array<{ product?: SettingsProduct }>;
      const product = routes.find((r) => String(r.product?.id) === selectedApiId)?.product;
      return product ? { id: product.id, pid: product.pid, microservices: product.microservices ?? [] } : null;
    } catch {
      return null;
    }
  }
}
